import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ArrowRight, CheckCircle2, Calendar, Building2 } from 'lucide-react';
import { api } from '@/lib/api';
import { log } from '@/lib/log';
import { toast } from 'sonner';

const TEAM_SIZES = ['1–10', '11–50', '51–200', '201–1000', '1000+'];
const ROLES = [
  'Founder / CEO',
  'VP / Head of Sales',
  'VP / Head of Marketing',
  'VP / Head of Operations',
  'CTO / Head of Engineering',
  'Investor',
  'Other',
];

export const BookDemoModal = ({ open, onClose, source = 'book_demo' }) => {
  const [form, setForm] = useState({
    email: '', name: '', company: '', role: ROLES[0], team_size: TEAM_SIZES[2], use_case: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.email || !form.name || !form.company) {
      toast.error('Email, name and company are required');
      return;
    }
    setSubmitting(true);
    try {
      await api.post('/leads/create', { ...form, source });
      setDone(true);
      toast.success('Demo request received — we’ll reach out within 1 business day.');
    } catch (err) {
      log.warn('lead create failed', err);
      toast.error(err?.response?.data?.detail || 'Could not submit. Try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const close = () => {
    onClose?.();
    setTimeout(() => setDone(false), 400);
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          className="fixed inset-0 z-[110] grid place-items-center p-4"
        >
          <div className="absolute inset-0 bg-black/70 backdrop-blur-md" onClick={close} />
          <motion.div
            initial={{ y: 20, opacity: 0, scale: 0.97 }}
            animate={{ y: 0, opacity: 1, scale: 1 }}
            exit={{ y: 20, opacity: 0, scale: 0.97 }}
            transition={{ type: 'spring', damping: 22, stiffness: 220 }}
            className="relative w-full max-w-2xl card-base p-7 overflow-hidden"
            data-testid="book-demo-modal"
          >
            <button onClick={close} className="absolute top-3 right-3 btn-ghost p-1.5 rounded"><X className="h-4 w-4" /></button>
            <div className="absolute -right-12 -top-16 h-56 w-56 rounded-full bg-[#FF6B1A]/15 blur-3xl pointer-events-none" />
            <div className="absolute -left-10 -bottom-16 h-44 w-44 rounded-full bg-[#39FF14]/10 blur-3xl pointer-events-none" />

            {!done ? (
              <div className="relative">
                <div className="h-11 w-11 rounded-xl grid place-items-center bg-gradient-to-br from-[#FF6B1A] to-[#FF8A4A] mb-4">
                  <Calendar className="h-5 w-5 text-white" />
                </div>
                <span className="text-[10px] uppercase tracking-widest text-[#39FF14]">// talk to the founders</span>
                <h3 className="display text-2xl font-semibold mt-1 leading-tight">Book an Enterprise demo.</h3>
                <p className="text-sm text-white/65 mt-2 max-w-md">
                  30 minutes. We’ll wire your top 3 revenue workflows and project your first-90-day ROI. No deck. No fluff.
                </p>
                <form onSubmit={onSubmit} className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <Field label="Work email *">
                    <input type="email" required value={form.email} onChange={(e) => set('email', e.target.value)} placeholder="you@company.com" className="input" data-testid="book-demo-email" />
                  </Field>
                  <Field label="Full name *">
                    <input required value={form.name} onChange={(e) => set('name', e.target.value)} placeholder="Jane Doe" className="input" />
                  </Field>
                  <Field label="Company *">
                    <input required value={form.company} onChange={(e) => set('company', e.target.value)} placeholder="NorthSignal Inc" className="input" />
                  </Field>
                  <Field label="Role">
                    <select value={form.role} onChange={(e) => set('role', e.target.value)} className="input">
                      {ROLES.map((r) => <option key={r}>{r}</option>)}
                    </select>
                  </Field>
                  <Field label="Team size">
                    <select value={form.team_size} onChange={(e) => set('team_size', e.target.value)} className="input">
                      {TEAM_SIZES.map((t) => <option key={t}>{t}</option>)}
                    </select>
                  </Field>
                  <div className="sm:col-span-2">
                    <Field label="What revenue workflow do you want to automate first?">
                      <textarea rows={3} value={form.use_case} onChange={(e) => set('use_case', e.target.value)}
                        placeholder="e.g. Outbound to mid-market fintech, with personalized 3-step sequences and BANT qualification."
                        className="input" />
                    </Field>
                  </div>
                  <div className="sm:col-span-2 flex flex-wrap items-center justify-between gap-3 mt-1">
                    <div className="text-[11px] text-white/45 inline-flex items-center gap-1.5">
                      <Building2 className="h-3.5 w-3.5" /> All inquiries handled by founders directly. No drip sequence.
                    </div>
                    <button type="submit" disabled={submitting} data-testid="book-demo-submit"
                      className="btn-primary inline-flex items-center gap-2 rounded-xl px-5 py-3 text-sm font-medium disabled:opacity-60">
                      {submitting ? 'Submitting…' : <>Request demo <ArrowRight className="h-4 w-4" /></>}
                    </button>
                  </div>
                </form>
              </div>
            ) : (
              <div className="relative text-center py-4">
                <div className="h-14 w-14 mx-auto rounded-2xl grid place-items-center bg-gradient-to-br from-[#39FF14] to-[#A8FFCB]" style={{ boxShadow: '0 0 32px rgba(57,255,20,0.45)' }}>
                  <CheckCircle2 className="h-7 w-7 text-[#0f1115]" />
                </div>
                <span className="text-[10px] uppercase tracking-widest text-[#39FF14] mt-4 block">// you’re on the list</span>
                <h3 className="display text-2xl font-semibold mt-2">Talk to you within 24 hours.</h3>
                <p className="text-sm text-white/65 mt-2 max-w-md mx-auto">
                  We just received your request. A founder will reach out at the address you provided. In the meantime, kick the tires — every marketplace agent is free to try.
                </p>
                <a
                  href="mailto:info@revcoreai.com"
                  className="mt-4 inline-flex items-center gap-2 text-xs text-white/55 hover:text-[#39FF14] transition-colors"
                >
                  Can’t wait? Email us at <span className="mono text-[#39FF14]">info@revcoreai.com</span>
                </a>
                <button onClick={close} className="btn-primary mt-5 rounded-xl px-5 py-3 text-sm font-medium">Close</button>
              </div>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

function Field({ label, children }) {
  return (
    <label className="block">
      <div className="text-xs text-white/55 mb-1.5">{label}</div>
      {children}
    </label>
  );
}
